import EntitySettings from './entitySettings';
import * as _ from 'lodash';
import { PAGE_COLLECTIONS, PAGE_INDEX, PAGE_PRODUCTS, PAGE_SEARCH } from '../app-library';
import * as C from '../../../../assets/js/_constants';

export default class AppConfig {
  /**
   * @type {null|boolean}
   */
  #discountsEnabledCache = null;

  /**
   * @param {Env} env
   * @param {Settings} settings
   * @param {Snippet} snippet
   * @param {Customization} customization
   */
  constructor (env, settings, snippet, customization) {
    /**
     * @return {Env}
     */
    this.env = () => env;

    /**
     * @return {Settings}
     */
    this.settings = () => settings;

    /**
     * @return {Snippet}
     */
    this.snippet = () => snippet;

    /**
     * @return {Customization}
     */
    this.customization = () => customization;

    this.productSettings = new EntitySettings('products', this.settings);
    this.variantSettings = new EntitySettings('variants', this.settings);
  }

  /**
   *
   */
  init () {
    this.settings().init();
    this.customization().onConfigReady(this);
  }

  /**
   * @param {string} page
   * @return {string}
   */
  getATCSelector (page) {
    let selectorType;
    if (page === PAGE_PRODUCTS) {
      selectorType = 'product';
    } else if (page === PAGE_COLLECTIONS || page === PAGE_SEARCH) {
      selectorType = 'collection';
    } else if (page === PAGE_INDEX) {
      selectorType = 'index';
    } else {
      selectorType = 'quickview';
    }

    let customSelector = this.customization().getCustomSelectorForPage(selectorType);
    if (!!customSelector) {
      return customSelector;
    }

    let quickviewSelector = this.settings().config.selectors['quickview'].selector;
    if (selectorType === 'quickview') {
      return quickviewSelector;
    }
    let selector = this.settings().config.selectors[selectorType].selector;
    if (quickviewSelector && quickviewSelector !== selector) {
      if (selector) {
        selector += ', ';
      }
      selector += quickviewSelector;
    }
    return selector;
  }

  /**
   * @param {string} page
   * @return {string}
   */
  getProductPriceSelector (page) {
    let selectorType;
    if (page === PAGE_PRODUCTS) {
      selectorType = 'product';
    } else if (page === PAGE_COLLECTIONS || page === PAGE_SEARCH) {
      selectorType = 'collection';
    } else if (page === PAGE_INDEX) {
      selectorType = 'index';
    } else {
      selectorType = 'quickview';
    }

    let customSelector = this.customization().getCustomProductPriceSelectorForPage(selectorType);
    if (!!customSelector) {
      return customSelector;
    }

    let quickviewSelector = this.settings().config.selectors_discount['quickview'].price_product;
    if (selectorType === 'quickview') {
      return quickviewSelector;
    }
    let selector = this.settings().config.selectors_discount[selectorType].price_product;
    if (quickviewSelector && quickviewSelector !== selector) {
      if (selector) {
        selector += ', ';
      }
      selector += quickviewSelector;
    }
    return selector;
  }

  /**
   * @return {{}}
   */
  defaultSetting () {
    return this.settings().defaultSetting;
  }

  /**
   * @return {boolean}
   */
  get discountsEnabled () {
    if (this.#discountsEnabledCache !== null) {
      return this.#discountsEnabledCache;
    }

    // Check general default discount
    this.#discountsEnabledCache = !!this.settings().defaultSetting.discount.value;

    // Check products discount
    if (!this.#discountsEnabledCache) {
      this.#discountsEnabledCache = Object.values(this.settings().products)
        .some(product => typeof (product.discount) !== 'undefined' && !!product.discount.value);
    }

    // Check variants discount
    if (!this.#discountsEnabledCache) {
      this.#discountsEnabledCache = Object.values(this.settings().variants)
        .some(variant => typeof (variant.discount) !== 'undefined' && !!variant.discount.value);
    }

    return this.#discountsEnabledCache;
  }

  /**
   * @param {number} variantId
   * @returns {{config:{preOrderLabel:string}, date_start, date_stop, is_active: boolean, discount: {value: number|null, type: string}}}
   */
  getSettingsByVariantId (variantId) {
    const defaultSettings = {
      is_active: false,
      config: this.settings().defaultSetting.config || {},
      date_start: this.settings().defaultSetting.date_start || null,
      date_stop: this.settings().defaultSetting.date_stop || null,
    };

    let settings = defaultSettings;

    const productId = this.snippet().getProductIdByVariantId(variantId);
    if (productId) {
      let productSettings = this.productSettings.getSettingsById(productId);
      if (productSettings) {
        if (productSettings.config || productSettings.date_start || productSettings.date_stop || productSettings.discount.value) {
          settings = productSettings;
        } else {
          settings.is_active = productSettings.is_active;
        }
      }
    }

    let variantSettings = this.variantSettings.getSettingsById(variantId);
    if (variantSettings) {
      if (variantSettings.config || variantSettings.date_start || variantSettings.date_stop || variantSettings.discount.value) {
        settings = variantSettings;
      } else {
        settings.is_active = variantSettings.is_active;
      }
    }

    _.defaultsDeep(settings, defaultSettings);

    if (settings.config === null) {
      settings.config = defaultSettings.config;
    }

    if (!settings.config.hasCustomCheckoutLabel) {
      settings.config.preOrderLabel = settings.config.buttonMessage;
    }

    settings.discount = this.getDiscountByVariantId(variantId);

    return settings;
  }

  /**
   * @param {number} variantId
   * @returns {{value: number|null, type: string}}
   */
  getDiscountByVariantId (variantId) {
    const defaultDiscount = {
      value: this.settings().defaultSetting.discount.value || 0,
      type: this.settings().defaultSetting.discount.type || C.DISCOUNT_TYPE_PERCENTAGE
    };

    const variantSettings = this.variantSettings.getSettingsById(variantId);
    if (variantSettings && variantSettings.discount) {
      if (variantSettings.discount.value === null) {
        return defaultDiscount;
      }

      return variantSettings.discount;
    }

    const productId = this.snippet().getProductIdByVariantId(variantId);
    if (productId) {
      const productSettings = this.productSettings.getSettingsById(productId);
      if (productSettings && productSettings.discount) {
        if (productSettings.discount.value === null) {
          return defaultDiscount;
        }

        return productSettings.discount;
      }
    }

    return defaultDiscount;
  }
}